import React from "react";
import { motion } from "framer-motion";
import { Store, Ticket, ReceiptText, Loader2, CheckCircle2, FileText, ShieldCheck } from "lucide-react";
import StatusBadge from "../StatusBadge";

const KYC_DOCS = [
  { key: "pan", label: "PAN Card" },
  { key: "gst", label: "GST Certificate" },
  { key: "business", label: "Business Proof" },
];

export function Stat({ label, value, testid, accent }) {
  return (
    <div className="ledger-card rounded-xl p-5">
      <p className="font-mono text-xs uppercase tracking-widest text-slate-500">{label}</p>
      <p data-testid={testid} className={`mt-2 font-heading text-3xl font-extrabold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{Number(value).toLocaleString("en-IN")}</p>
    </div>
  );
}

export function StatsRow({ m, plan }) {
  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-3">
      <Stat label="Available tokens" value={m?.available_tokens ?? 0} testid="merchant-available-tokens" accent />
      <Stat label="Total tokens" value={m?.total_tokens ?? 0} testid="merchant-total-tokens" />
      <div className="ledger-card rounded-xl p-5">
        <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Subscription</p>
        <div className="mt-2"><StatusBadge status={m?.subscription_status === "ACTIVE" ? "ACTIVE" : "REQUESTED"} /></div>
        <p className="mt-2 text-sm text-slate-400">{plan?.name || "No active plan"}</p>
      </div>
    </div>
  );
}

export function NeedProfileForm({ biz, setBiz, createProfile, busy }) {
  return (
    <div className="mx-auto max-w-lg ledger-card rounded-2xl p-8">
      <Store className="h-8 w-8 text-emerald-400" />
      <h2 className="mt-4 font-heading text-2xl font-bold">Create your merchant profile</h2>
      <p className="mt-1 text-sm text-slate-400">Tell us about your business to start.</p>
      <div className="mt-6 space-y-3">
        {[["business_name", "Business name"], ["gst", "GSTIN (optional)"], ["pan", "PAN (optional)"], ["bank_account", "Bank account (optional)"]].map(([k, l]) => (
          <input key={k} data-testid={`biz-${k}`} placeholder={l} value={biz[k]}
            onChange={(e) => setBiz({ ...biz, [k]: e.target.value })}
            className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
        ))}
      </div>
      <button data-testid="create-merchant-button" onClick={createProfile} disabled={busy}
        className="mt-6 w-full rounded-full bg-emerald-500 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
        {busy ? "Creating…" : "Create profile"}
      </button>
    </div>
  );
}

export function IssueTab({ m, form, setForm, issue, busy }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-lg ledger-card rounded-2xl p-8">
      <div className="flex items-center gap-2"><Ticket className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">Issue cashback tokens</h2></div>
      <p className="mt-1 text-sm text-slate-400">Just enter the customer's mobile and how many tokens. Each token is a scratch card — the customer reveals a random cashback (₹0–₹1000) as a lucky draw. Unregistered customers see them after sign-up.</p>
      {m?.subscription_status !== "ACTIVE" && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">No active subscription. Subscribe to get tokens.</div>
      )}
      <div className="mt-6 space-y-3">
        <input data-testid="issue-mobile" placeholder="Customer mobile number" value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-3 text-base outline-none focus:border-emerald-500" />
        <input data-testid="issue-token-count" type="number" min="1" placeholder="Number of tokens" value={form.token_count} onChange={(e) => setForm({ ...form, token_count: e.target.value })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-3 text-base outline-none focus:border-emerald-500" />
      </div>
      <p className="mt-3 text-xs text-slate-500">Tokens carry no fixed value — each is scratched by the customer to reveal a lucky-draw cashback reward.</p>
      <button data-testid="issue-token-submit" onClick={issue} disabled={busy || m?.subscription_status !== "ACTIVE"} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 text-base font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Issue tokens
      </button>
    </motion.div>
  );
}

export function SubscribeTab({ refApplied, refCode, setRefCode, applyReferral, busy, plans, subscribe, devActivate, appCfg }) {
  return (
    <div className="space-y-6">
      <div className="ledger-card rounded-2xl p-5" data-testid="merchant-referral-box">
        <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Have a referral code?</p>
        <p className="mt-1 text-sm text-slate-400">Enter the code a customer shared with you. The reward goes to them — it doesn't change your price.</p>
        {refApplied ? (
          <div className="mt-3 flex items-center gap-2 text-sm" data-testid="merchant-referral-applied">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span className="font-mono text-emerald-400">{refCode.trim().toUpperCase()}</span>
            <span className={refApplied.status === "INVALID" ? "text-amber-400" : "text-slate-400"}>
              {refApplied.status === "INVALID" ? "flagged (no reward)" : "applied"}
            </span>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            <input data-testid="merchant-referral-input" value={refCode}
              onChange={(e) => setRefCode(e.target.value.toUpperCase())}
              placeholder="PARAS123ABC"
              className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 font-mono text-sm uppercase outline-none focus:border-emerald-500" />
            <button data-testid="merchant-referral-apply" onClick={applyReferral} disabled={busy}
              className="rounded-full bg-emerald-500 px-5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">Apply</button>
          </div>
        )}
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {plans.map((p, i) => (
          <div key={p.plan_id} data-testid={`subscribe-plan-${p.plan_id}`} className={`ledger-card rounded-2xl p-6 ${i === 1 ? "ring-1 ring-emerald-500/40" : ""}`}>
            <h3 className="font-heading text-lg font-bold">{p.name}</h3>
            <p className="mt-2 font-heading text-3xl font-extrabold">{p.price_display}</p>
            <p className="text-xs text-slate-400">incl. 18% GST</p>
            <div className="mt-3 rounded-lg bg-slate-900/60 px-3 py-2 text-center font-mono text-sm text-emerald-400">{p.token_qty.toLocaleString("en-IN")} tokens</div>
            <button data-testid={`subscribe-button-${p.plan_id}`} onClick={() => subscribe(p.plan_id)} disabled={busy} className="mt-5 w-full rounded-full bg-emerald-500 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">Subscribe</button>
            {appCfg?.payments?.dev_enabled && (
              <button data-testid={`subscribe-dev-${p.plan_id}`} onClick={() => devActivate(p.plan_id)} disabled={busy} className="mt-2 w-full rounded-full border border-slate-700 py-2 text-xs text-slate-400 hover:border-emerald-500/50 disabled:opacity-50">Activate in test mode (no payment)</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function HistoryTab({ tokens }) {
  return (
    <div className="ledger-card overflow-hidden rounded-2xl">
      <div className="flex items-center gap-2 border-b border-slate-800 p-5"><ReceiptText className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-lg font-bold">Issued tokens</h2></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="merchant-tokens-table">
          <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-4 text-left">Date</th><th className="p-4 text-left">Mobile</th><th className="p-4 text-right">Tokens</th><th className="p-4 text-right">Cashback</th><th className="p-4 text-left">State</th></tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.tokenId} className="border-t border-slate-800/60">
                <td className="p-4 text-slate-400">{new Date(t.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-4">{t.customer_mobile}</td>
                <td className="p-4 text-right font-mono">{t.token_count ?? 1}</td>
                <td className="p-4 text-right font-mono text-emerald-400">₹{(t.cashback_paise / 100).toLocaleString("en-IN")}</td>
                <td className="p-4"><StatusBadge status={t.state} /></td>
              </tr>
            ))}
            {tokens.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No tokens issued yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function KycTab({ kyc, uploadKyc }) {
  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="merchant-kyc">
      <div className="ledger-card rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">KYC verification</h2></div>
          <StatusBadge status={(kyc?.kyc_status === "PENDING" ? "PENDING_KYC" : kyc?.kyc_status) || "PENDING_KYC"} />
        </div>
        <p className="mt-2 text-sm text-slate-400">Upload your PAN, GST certificate and a business proof. Our team reviews and approves them (PDF/JPG/PNG, max 5MB each).</p>
        {kyc?.kyc_status === "REJECTED" && kyc?.kyc_note && (
          <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300" data-testid="kyc-reject-note">Rejected: {kyc.kyc_note}</div>
        )}
        {kyc?.kyc_status === "VERIFIED" && (
          <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">Your KYC is verified ✓</div>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {KYC_DOCS.map(({ key, label }) => {
          const doc = (kyc?.documents || []).find((d) => d.doc_type === key);
          return (
            <div key={key} className="ledger-card rounded-2xl p-5" data-testid={`kyc-slot-${key}`}>
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-emerald-400" /><p className="text-sm font-semibold">{label}</p></div>
              {doc ? (
                <p className="mt-2 truncate text-xs text-emerald-400" title={doc.original_filename}>✓ {doc.original_filename}</p>
              ) : (
                <p className="mt-2 text-xs text-slate-500">Not uploaded</p>
              )}
              <label className="mt-3 block cursor-pointer rounded-full border border-slate-700 py-2 text-center text-xs hover:border-emerald-500/50">
                {doc ? "Replace" : "Upload"}
                <input data-testid={`kyc-file-${key}`} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadKyc(key, e.target.files[0])} />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
